import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Switch,
  FormControlLabel,
  SelectChangeEvent,
  Alert
} from '@mui/material';
import { getVesselById, updateVessel } from '../../firebase/services/vesselService';
import { Vessel } from '../../models/types';
import MarinaAdminLayout from '../../layouts/MarinaAdminLayout';

const EditVesselPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [vessel, setVessel] = useState<Vessel | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    type: '',
    propulsionType: 'powered' as 'powered' | 'non-powered',
    capacity: '',
    description: '',
    isActive: true,
    images: [] as string[],
    amenities: [] as string[],
    manufacturer: '',
    registrationNumber: '',
    datePlacedInService: '',
    engineHorsepower: '',
    engineSerialNumber: '',
    length: '',
    year: '',
    model: '',
    stateRegistrationExpiry: '',
    stateRegistrationState: ''
  });

  useEffect(() => {
    const fetchVesselData = async () => {
      if (!id) return;
      
      try {
        const vessel = await getVesselById(id);
        
        if (vessel) {
          setVessel(vessel);
          setFormData({
            name: vessel.name,
            type: vessel.type,
            propulsionType: vessel.propulsionType || 'powered',
            capacity: vessel.capacity.toString(),
            description: vessel.description || '',
            isActive: vessel.isActive,
            images: vessel.images || [],
            amenities: vessel.amenities || [],
            manufacturer: vessel.manufacturer || '',
            registrationNumber: vessel.registrationNumber || '',
            datePlacedInService: vessel.datePlacedInService || '',
            engineHorsepower: vessel.engineHorsepower ? vessel.engineHorsepower.toString() : '',
            engineSerialNumber: vessel.engineSerialNumber || '',
            length: vessel.length ? vessel.length.toString() : '',
            year: vessel.year ? vessel.year.toString() : '',
            model: vessel.model || '',
            stateRegistrationExpiry: vessel.stateRegistrationExpiry || '',
            stateRegistrationState: vessel.stateRegistrationState || ''
          });
        } else {
          setError('Vessel not found');
        }
      } catch (err) {
        console.error('Error fetching vessel data:', err);
        setError('Failed to load vessel data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchVesselData();
  }, [id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (vessel) {
      setVessel({ ...vessel, [e.target.name]: e.target.value });
    }
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    if (vessel) {
      setVessel({ ...vessel, [e.target.name]: e.target.value });
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vessel || !id) return;

    setLoading(true);
    try {
      await updateVessel(id, vessel);
      navigate('/admin/vessels');
    } catch (error) {
      console.error('Error updating vessel:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MarinaAdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      </MarinaAdminLayout>
    );
  }

  return (
    <MarinaAdminLayout>
      <Box sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Vessel updated successfully! Redirecting...
          </Alert>
        )}
        
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Edit Vessel
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Vessel Name"
                    name="name"
                    value={vessel?.name || ''}
                    onChange={handleChange}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Vessel Type</InputLabel>
                    <Select
                      name="type"
                      value={vessel?.type || ''}
                      onChange={handleSelectChange}
                    >
                      <MenuItem value="pontoon">Pontoon</MenuItem>
                      <MenuItem value="fishing boat">Fishing Boat</MenuItem>
                      <MenuItem value="speedboat">Speedboat</MenuItem>
                      <MenuItem value="jet ski">Jet Ski</MenuItem>
                      <MenuItem value="kayak">Kayak</MenuItem>
                      <MenuItem value="canoe">Canoe</MenuItem>
                      <MenuItem value="paddleboard">Paddleboard (SUP)</MenuItem>
                      <MenuItem value="rowboat">Rowboat</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Propulsion Type</InputLabel>
                    <Select
                      name="propulsionType"
                      value={vessel?.propulsionType || 'powered'}
                      onChange={handleSelectChange}
                    >
                      <MenuItem value="powered">Powered</MenuItem>
                      <MenuItem value="non-powered">Non-Powered</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Capacity (persons)"
                    name="capacity"
                    type="number"
                    value={vessel?.capacity?.toString() || ''}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    multiline
                    rows={3}
                    value={vessel?.description || ''}
                    onChange={handleChange}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Manufacturer"
                    name="manufacturer"
                    value={vessel?.manufacturer || ''}
                    onChange={handleChange}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Registration Number"
                    name="registrationNumber"
                    value={vessel?.registrationNumber || ''}
                    onChange={handleChange}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Date Placed in Service"
                    name="datePlacedInService"
                    type="date"
                    value={vessel?.datePlacedInService || ''}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Length (feet)"
                    name="length"
                    type="number"
                    value={vessel?.length?.toString() || ''}
                    onChange={handleChange}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Year"
                    name="year"
                    type="number"
                    value={vessel?.year?.toString() || ''}
                    onChange={handleChange}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Model"
                    name="model"
                    value={vessel?.model || ''}
                    onChange={handleChange}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="State Registration Expiry"
                    name="stateRegistrationExpiry"
                    type="date"
                    value={vessel?.stateRegistrationExpiry || ''}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Registration State</InputLabel>
                    <Select
                      name="stateRegistrationState"
                      value={vessel?.stateRegistrationState || ''}
                      onChange={handleSelectChange}
                    >
                      {/* Add your state options here */}
                    </Select>
                  </FormControl>
                </Grid>
                
                {vessel?.propulsionType === 'powered' && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                        Engine Information
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Engine Horsepower"
                        name="engineHorsepower"
                        type="number"
                        value={vessel?.engineHorsepower?.toString() || ''}
                        onChange={handleChange}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Engine Serial Number"
                        name="engineSerialNumber"
                        value={vessel?.engineSerialNumber || ''}
                        onChange={handleChange}
                      />
                    </Grid>
                  </>
                )}
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={vessel?.isActive || true}
                        onChange={handleSwitchChange}
                        name="isActive"
                      />
                    }
                    label="Active Status"
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="button"
                  onClick={() => navigate('/admin/equipment/vessels')}
                  sx={{ mr: 2 }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Save Changes'}
                </Button>
              </Box>
            </form>
          )}
        </Paper>
      </Box>
    </MarinaAdminLayout>
  );
};

export default EditVesselPage; 